import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { WarningFilled } from '@siftscience/focus-components/icons/WarningFilled'
import { KnowledgeBase } from '@siftscience/focus-components/icons/KnowledgeBase'
import { Export } from '@siftscience/focus-components/icons/Export'
import { Text } from '@siftscience/focus-components/text'
import { Button } from '@siftscience/focus-components/button'
import { TextInput } from '@siftscience/focus-components/input'
import { DataField } from '../dtos'
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext'

const ERROR_ICON_COLOR = '#EB3B2E'

const useStyles = makeStyles(() => ({
  noImageFieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    flexGrow: 1,
    padding: '16px 46px',
    gap: '8px',
    border: '1px dashed #E0E0E0'
  },
  inputArea: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px'
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  error: {
    position: 'absolute',
    top: '10px',
    right: '10px'
  },
  icon: {
    width: '16px',
    height: '16px'
  }
}))

interface UploadBlockProps {
  imageError: boolean
  isWorkspace: boolean
  dataField: DataField
  focusedField: { name: string; isArray: boolean }
  imageRef: React.Ref<HTMLInputElement>
  handleSelectImage: (event) => void
  handleDragOver: (event) => void
  handleDrop: (event) => void
  onUploadClick: (event) => void
  onLibraryClick: (field: DataField) => void
  handlePaste: (event) => void
}

const UploadBlock = ({
  imageError,
  isWorkspace,
  dataField,
  focusedField,
  imageRef,
  handleSelectImage,
  handleDragOver,
  handleDrop,
  onUploadClick,
  onLibraryClick,
  handlePaste
}: UploadBlockProps): React.ReactElement => {
  const classes = useStyles()
  const { imageLibrary } = useFeatureFlags()

  const onUpload = event => {
    if (isWorkspace) {
      onUploadClick(event)
    }
  }

  const onDragOver = event => {
    if (isWorkspace) {
      handleDragOver(event)
    }
  }

  const onDrop = event => {
    if (isWorkspace) {
      handleDrop(event)
    }
  }

  return (
    <div
      className={classes.noImageFieldWrapper}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {imageError && (
        <div className={classes.error}>
          <WarningFilled color={ERROR_ICON_COLOR} className={classes.icon} />
        </div>
      )}
      {isWorkspace ? (
        <Text size="small">
          Drag and drop, upload or paste image here
          {imageLibrary && ' or choose one from the library'}
        </Text>
      ) : null}
      <div className={classes.inputArea}>
        <div className={classes.flex}>
          {isWorkspace ? (
            <Button variant="secondary" onClick={onUpload}>
              <Export /> Upload
            </Button>
          ) : null}
          {imageLibrary && (
            <Button
              variant="secondary"
              onClick={() => onLibraryClick(dataField)}
            >
              <KnowledgeBase /> Library
            </Button>
          )}
        </div>
        {isWorkspace ? (
          <TextInput
            containerStyle={{ width: '188px' }}
            value="Click to paste image here"
            onPaste={handlePaste}
            autoFocus={dataField?.name === focusedField?.name}
          />
        ) : null}
      </div>
      <input
        ref={imageRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleSelectImage}
      />
    </div>
  )
}

export default UploadBlock
